import React, { useState } from "react";
import { Box, Typography, Button, IconButton, Tooltip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { StyledDialogContent } from "./styles";

interface CompletedScreenProps {
  completedRequestId: string;
  onClose: () => void;
}

/**
 * リクエスト受付完了時に表示される画面。
 */
export const CompletedScreen: React.FC<CompletedScreenProps> = ({
  completedRequestId,
  onClose,
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyRequestId = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(completedRequestId);
      setIsCopied(true);
      toast.success("リクエスト ID をコピーしました");
      window.setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("コピーに失敗しました");
    }
  };

  return (
    <StyledDialogContent>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          py: { xs: 3, sm: 5, md: 6 },
          px: { xs: 1, sm: 2 },
        }}
      >
        {/* ヘッダー部分 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 2, sm: 3 },
            mb: { xs: 4, sm: 5, md: 6 },
            p: { xs: 3, sm: 4, md: 5 },
            backgroundColor: "#ffffff",
            borderRadius: { xs: "16px", sm: "20px" },
            border: "1px solid #e5e7eb",
            width: "100%",
            maxWidth: { xs: "100%", sm: "500px", md: "600px" },
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          {/* アイコン部分 */}
          <Box
            sx={{
              width: { xs: 60, sm: 70, md: 80 },
              height: { xs: 60, sm: 70, md: 80 },
              borderRadius: "50%",
              backgroundColor: "#3b82f6",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{
                color: "#ffffff",
                fontSize: { xs: 30, sm: 35, md: 40 },
              }}
            />
          </Box>

          {/* テキスト部分 */}
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.25rem", sm: "1.4rem", md: "1.5rem" },
                color: "#1f2937",
                mb: 1,
                lineHeight: 1.2,
              }}
            >
              リクエスト受付完了
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#6b7280",
                fontSize: { xs: "0.9rem", sm: "1rem" },
                fontWeight: 500,
                mt: 2,
              }}
            >
              取得を開始いたします
            </Typography>
          </Box>
        </Box>

        {/* メッセージ部分 */}
        <Box
          sx={{
            mb: { xs: 4, sm: 5 },
            maxWidth: { xs: "100%", sm: "600px", md: "700px" },
            px: { xs: 1, sm: 2 },
          }}
        >
          <Typography
            sx={{
              color: "#374151",
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
              lineHeight: 1.7,
              mb: 0,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            不動産資料取得のリクエストを受け付けいたしました。
            <br />
            今後の進捗につきましては、メールにてご連絡いたします。
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: 1.6,
              mt: { xs: 3, sm: 4 },
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            お問い合わせの際は下記のリクエストIDをお伝えいただくと、
            <br />
            よりスムーズにご対応いたします。
          </Typography>
        </Box>

        {/* リクエストID表示 */}
        <Box
          sx={{
            backgroundColor: "#f1f5f9",
            borderRadius: { xs: 2, sm: 3 },
            p: { xs: 3, sm: 4 },
            border: "2px solid #cbd5e1",
            mb: { xs: 5, sm: 6 },
            width: "100%",
            maxWidth: { xs: "100%", sm: "600px", md: "700px" },
            textAlign: "center",
            boxShadow:
              "0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 1px 2px -1px rgba(0, 0, 0, 0.06)",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              fontWeight: 600,
              mb: { xs: 2, sm: 2.5 },
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              textAlign: "center",
            }}
          >
            リクエスト ID
          </Typography>
          <Tooltip
            title={isCopied ? "コピーしました" : "クリックでコピー"}
            placement="top"
            arrow
          >
            <Box
              role="button"
              tabIndex={0}
              aria-label="リクエスト ID をクリップボードにコピー"
              onClick={() => {
                void handleCopyRequestId();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  void handleCopyRequestId();
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                cursor: "pointer",
                backgroundColor: "#ffffff",
                padding: { xs: 2, sm: 2.5 },
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#f8fafc",
                  borderColor: "#cbd5e1",
                },
                "&:focus-visible": {
                  outline: "2px solid #3b82f6",
                  outlineOffset: "2px",
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                  color: "#1e293b",
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  letterSpacing: "0.025em",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                {completedRequestId}
              </Typography>
              <IconButton
                size="small"
                aria-hidden="true"
                tabIndex={-1}
                sx={{ flexShrink: 0, color: isCopied ? "#16a34a" : "#64748b" }}
              >
                {isCopied ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Tooltip>
        </Box>

        {/* ボタン */}
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            minWidth: { xs: "200px", sm: "140px" },
            height: "48px",
            fontSize: "0.95rem",
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          確認しました
        </Button>
      </Box>
    </StyledDialogContent>
  );
};
